import * as stylex from '@stylexjs/stylex'
import { type ComponentPropsWithoutRef, type ForwardedRef, type PropsWithChildren } from 'react'
import { useIsExited } from '../useIsExited/useIsExited'
import { useXStyles, type XStyles } from '../useXStyles/useXStyles'

const slideUpIn = stylex.keyframes({
  '0%': { transform: 'translateY(100%)' },
  '100%': { transform: 'translateY(0)' },
})

const slideDownIn = stylex.keyframes({
  '0%': { transform: 'translateY(-100%)' },
  '100%': { transform: 'translateY(0)' },
})

const slideLeftIn = stylex.keyframes({
  '0%': { transform: 'translateX(100%)' },
  '100%': { transform: 'translateX(0)' },
})

const slideRightIn = stylex.keyframes({
  '0%': { transform: 'translateX(-100%)' },
  '100%': { transform: 'translateX(0)' },
})

const slideUpOut = stylex.keyframes({
  '0%': { transform: 'translateY(0)' },
  '100%': { transform: 'translateY(100%)' },
})

const slideDownOut = stylex.keyframes({
  '0%': { transform: 'translateY(0)' },
  '100%': { transform: 'translateY(-100%)' },
})

const slideLeftOut = stylex.keyframes({
  '0%': { transform: 'translateX(0)' },
  '100%': { transform: 'translateX(100%)' },
})

const slideRightOut = stylex.keyframes({
  '0%': { transform: 'translateX(0)' },
  '100%': { transform: 'translateX(-100%)' },
})

const _sideInStyles = stylex.create({
  up: {
    animationName: slideUpIn,
  },
  down: {
    animationName: slideDownIn,
  },
  left: {
    animationName: slideLeftIn,
  },
  right: {
    animationName: slideRightIn,
  },
})

const sideOutStyles = stylex.create({
  up: {
    animationName: slideUpOut,
  },
  down: {
    animationName: slideDownOut,
  },
  left: {
    animationName: slideLeftOut,
  },
  right: {
    animationName: slideRightOut,
  },
})

const baseStyles = stylex.create({
  root: {
    animationDuration: '225ms',
    animationFillMode: 'forwards',
    animationTimingFunction: 'cubic-bezier(0, 0, 0.2, 1)',
  },
})

type Direction = 'left' | 'right' | 'up' | 'down'

export interface SlideProps extends ComponentPropsWithoutRef<'div'> {
  /** 根元素的 ref */
  rootRef?: ForwardedRef<HTMLDivElement>
  /** 控制滑入（true）或滑出（false），動畫結束後會卸載 */
  in?: boolean
  /**
   * 滑動方向
   * @default 'down'
   */
  direction?: Direction
  /** 自訂根元素的 StyleX 樣式 */
  rootStyle?: stylex.StyleXStyles
  /** 覆蓋滑入方向樣式的 xStyles */
  xStyles?: Partial<XStyles<keyof typeof _sideInStyles>>
  /** 滑出動畫結束後的回呼 */
  onExited?: () => void
}

/**
 * Slide 是滑動過渡動畫元件，支援四個方向（上下左右）的滑入滑出。
 * 透過 in prop 控制顯示與隱藏，動畫結束後元件自動卸載。
 */
export const Slide = (props: PropsWithChildren<SlideProps>) => {
  const { rootRef, in: inProp, direction = 'down', rootStyle, xStyles, children, onAnimationStart, onAnimationEnd, onExited, ...rest } = props
  const sideInStyles = useXStyles(_sideInStyles, xStyles)
  const { isExited, ...animationHandlers } = useIsExited({
    show: inProp,
    onAnimationStart,
    onAnimationEnd,
    onExited,
  })

  if (!inProp && isExited) return null

  return (
    <div
      ref={rootRef}
      {...stylex.props([
        baseStyles.root,
        inProp ? sideInStyles[direction] : sideOutStyles[direction],
        rootStyle,
      ])}
      {...animationHandlers}
      {...rest}
    >
      {children}
    </div>
  )
}
